import { UserOutlined } from '@ant-design/icons';
import { EnumPersonaFisicaRequestDTORuolo } from '@framework/api-client/api/backoffice/schemas/enumPersonaFisicaRequestDTORuolo.ts';
import { type PersonaFisicaRequestDTO } from '@framework/api-client/api/backoffice/schemas/personaFisicaRequestDTO.ts';
import { type PersonaFisicaDTO } from '@framework/api-client/api/inquiries-compile/schemas/personaFisicaDTO.ts';
import { ButtonDeleteConfirm } from '@framework/ui/components/button/ButtonDeleteConfirm.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { type ColumnType } from 'antd/es/table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DossierPermissionRoleTag } from './DossierPermissionRoleTag.tsx';

interface IDossierPermissionTableProps {
  data: PersonaFisicaDTO[];
  onRemove: (id: string | null | undefined) => Promise<void>;
  loading?: boolean;
  readonly?: boolean;
}

/**
 * Tabella dell'elenco dei permessi sulle pratiche.
 * (Cruscotto frontoffice)
 */
export function DossierPermissionTable(props: IDossierPermissionTableProps) {
  const { data, onRemove, loading, readonly } = props;

  const { t } = useTranslation();

  const tableColumns = useMemo(() => {
    if (readonly) return columns;
    return [
      ...columns,
      {
        dataIndex: 'actions',
        title: 'Azioni',
        align: 'right',
        key: 'actions',
        render: (_, record: PersonaFisicaRequestDTO) => {
          if (record.ruolo === EnumPersonaFisicaRequestDTORuolo.TITOLARE) {
            return null;
          }

          return (
            <ButtonDeleteConfirm
              onConfirm={async () => {
                await onRemove(record.id);
              }}
              title={t(
                'dossier-permission.delete-confirm-title',
                'Rimuovere il permesso?',
              )}
              description={t(
                'dossier-permission.delete-confirm-description',
                'Una volta rimosso, il soggetto non avrà più accesso alla pratica.',
              )}
              buttonType="link"
              buttonDanger
              // size="large"
              iconOnlyButton
            />
          );
        },
      } as ColumnType<PersonaFisicaRequestDTO>,
    ];
  }, [onRemove, readonly, t]);

  return (
    <TableData<PersonaFisicaRequestDTO>
      dataSource={data}
      columns={tableColumns}
      pagination={false}
      loading={loading}
      size="small"
    />
  );
}

const columns: Array<ColumnType<PersonaFisicaRequestDTO>> = [
  {
    title: 'Soggetto',
    dataIndex: 'subject',
    key: 'subject',
    render: (_, record) => {
      return (
        <div>
          <UserOutlined /> {record.nome} {record.cognome} (
          {record.codiceFiscale})
        </div>
      );
    },
  },
  {
    dataIndex: 'role',
    title: 'Ruolo',
    key: 'role',
    render: (_, record) => {
      // Non dovrebbe mai capitare
      if (!record.ruolo) {
        return <span>{record.ruolo}</span>;
      }
      return <DossierPermissionRoleTag role={record.ruolo} />;
    },
  },
];
